import * as React from 'react';
import { useSearchParams } from 'react-router-dom';
import { TravelBnplProviderSelection } from '@/components/payments/BNPLProviderSelection';
import SmartCheckoutRecommendation from '@/components/payments/SmartCheckoutRecommendation';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';

// Generate a stable idempotency key per checkout session
function useIdempotencyKey(): string {
  const [key] = React.useState(() => crypto.randomUUID());
  return key;
}

export default function TravelCheckoutPage() {
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const orderIdFromUrl = searchParams.get('orderId');
  const [loading, setLoading] = React.useState(true);
  const [order, setOrder] = React.useState<any | null>(null);
  const idempotencyKey = useIdempotencyKey();

  React.useEffect(() => {
    async function loadOrder() {
      if (!orderIdFromUrl) {
        setLoading(false);
        return;
      }

      const { data, error } = await supabase
        .from('orders')
        .select(`
          *,
          lead:leads(name, email, phone)
        `)
        .eq('id', orderIdFromUrl)
        .maybeSingle();

      if (error) {
        console.error(error);
        toast({
          title: 'Could not load booking',
          description: 'Please try starting your booking again.',
          variant: 'destructive',
        });
      } else {
        setOrder(data);
      }
      setLoading(false);
    }

    loadOrder();
  }, [orderIdFromUrl, toast]);

  if (loading) {
    return (
      <div className="mx-auto max-w-3xl p-6">
        <p>Loading your booking...</p>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="mx-auto max-w-3xl p-6">
        <p className="mb-4 text-sm text-muted-foreground">
          We could not find a booking linked to this page.
        </p>
        <Button asChild>
          <a href="/travel/search">Start a new search</a>
        </Button>
      </div>
    );
  }

  const totalAmount = order.amount_aed ?? 0;
  const currency = order.currency ?? 'AED';
  const customerName = order.lead?.name ?? '';
  const customerEmail = order.lead?.email ?? '';
  const customerPhone = order.lead?.phone ?? '';

  return (
    <div className="mx-auto flex max-w-5xl flex-col gap-8 p-4 md:p-8 lg:flex-row">
      <div className="flex-1 space-y-6">
        <h1 className="text-2xl font-semibold">Review and pay for your trip</h1>

        {/* Trip summary */}
        <div className="rounded-2xl border p-4">
          <h2 className="mb-2 text-lg font-medium">Trip summary</h2>
          <p className="text-sm text-muted-foreground">
            Booking reference: {order.id}
          </p>
          <p className="mt-1 text-sm">
            Total: <span className="font-semibold">{totalAmount} {currency}</span>
          </p>
        </div>

        {/* Smart BNPL recommendation */}
        <SmartCheckoutRecommendation
          vertical="travel"
          amount={totalAmount}
          currency={currency}
          merchantId={order.id}
          onSelect={(provider, plan) => {
            console.log("Selected provider:", provider, plan);
          }}
        />
      </div>

      <aside className="w-full max-w-sm space-y-4 rounded-2xl border p-4 md:p-6">
        <h2 className="text-lg font-semibold">Price breakdown</h2>
        <div className="flex justify-between text-sm">
          <span>Total</span>
          <span className="font-semibold">
            {totalAmount} {currency}
          </span>
        </div>
      </aside>
    </div>
  );
}
